import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Eye, EyeOff, X } from "lucide-react";
import { API_URL } from "../config/api";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import { maskDefinition } from "../utils/maskDefinition";
import MenuIconTooltip from "./MenuIconTooltip";
import {
  deleteWordIllustration,
  fetchWordIllustration,
  ILLUSTRATION_ACCEPT,
  prepareWordIllustration,
  uploadWordIllustration,
} from "../utils/wordIllustration";
import DeleteWordModal from "./DeleteWordModal";
import RenameWordSpellingModal from "./RenameWordSpellingModal";
import LiteraryExcerpt from "./LiteraryExcerpt";
import ToggleSwitch from "./ToggleSwitch";
import GroupementAssignDropdown from "./GroupementAssignDropdown";
import { assignWordGroupement, fetchGroupements } from "../utils/groupementsApi";
import {
  prepareWordFicheWrite,
  isBaseMotCache,
  motCacheAlreadyUsed,
  sanitizeMotsCaches,
  syncBaseMotCache,
  sanitizeWordLabel,
  sanitizeWordRichText,
  validateWordFicheFields,
  WORD_LIVRE_MAX_LENGTH,
  WORD_AUTEUR_MAX_LENGTH,
  WORD_RICH_TEXT_MAX_LENGTH,
  WORD_CACHE_MAX_LENGTH,
  WORD_CACHES_MAX,
} from "../utils/wordSanitize";
import WordFieldCharCount from "./WordFieldCharCount";
import useTaylorGothicReady from "../hooks/useTaylorGothicReady";
import "./WordEditPanel.css";
import "./GroupingsView.css";
import "./RegisterSkeleton.css";

function ficheFormFromWord(word = {}, fallbackGroupementId = "") {
  const motsCaches = sanitizeMotsCaches(word.motsCaches);
  return {
    definition: sanitizeWordRichText(word.definition),
    extrait: sanitizeWordRichText(word.extrait),
    motsCaches: motsCaches.length ? motsCaches : [""],
    livre: sanitizeWordLabel(word.livre),
    auteur: sanitizeWordLabel(word.auteur),
    groupementId: word.groupementId || fallbackGroupementId || "",
  };
}

function WordEditSkeleton() {
  return (
    <div className="word-edit-panel word-edit-panel--skeleton" aria-hidden="true">
      <h2
        className="word-detail-title"
        style={{
          textAlign: "center",
          margin: "0px 0 10px 0",
          fontSize: "calc(26px * var(--text-scale, 1))",
          color: "#39210d",
        }}
      >
        <span className="word-detail-title__initial">A</span>
        <span className="register-skel register-skel--title" />
      </h2>

      <div className="word-edit-groupement">
        <span className="register-skel register-skel--edit-tag" />
      </div>

      <div
        style={{
          maxWidth: "700px",
          margin: "0 auto",
          paddingLeft: "15px",
          boxSizing: "border-box",
        }}
      >
        <div className="word-edit-source-row">
          <div className="word-edit-source-fields">
            <div style={{ marginBottom: "20px" }}>
              <span className="register-skel register-skel--edit-heading" style={{ width: "78px" }} />
              <p className="word-edit-field-body">
                <span className="register-skel register-skel--line" style={{ width: "210px" }} />
              </p>
            </div>
            <div style={{ marginBottom: "0" }}>
              <span className="register-skel register-skel--edit-heading" style={{ width: "68px" }} />
              <p className="word-edit-field-body">
                <span className="register-skel register-skel--line" style={{ width: "132px" }} />
              </p>
            </div>
          </div>

          <div className="word-edit-illustration">
            <div className="word-edit-illustration__frame register-skel" />
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <span className="register-skel register-skel--edit-heading" style={{ width: "96px" }} />
          <div className="word-edit-skeleton__definition">
            {[100, 94, 97, 91, 88, 84, 62].map((width, index) => (
              <span
                key={index}
                className="register-skel register-skel--line"
                style={{ width: `${width}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const EDIT_ACTIONS_MENU_WIDTH = 280;

function EditActionsMenu({
  onEdit,
  onRenameSpelling,
  onDelete,
  canRenameSpelling = true,
  canDeleteWord = true,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const scale = getAppScale();
    const rect = trigger.getBoundingClientRect();
    const visualWidth = EDIT_ACTIONS_MENU_WIDTH * scale;
    let left = rect.left + rect.width / 2 - visualWidth / 2;
    const margin = 8;
    left = Math.max(margin, Math.min(left, window.innerWidth - visualWidth - margin));
    setCoords({
      top: rect.bottom + 8,
      left,
      width: EDIT_ACTIONS_MENU_WIDTH,
      transform: scale === 1 ? "none" : `scale(${scale})`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    // `true` : capte aussi le scroll des conteneurs internes.
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (listRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const options = [
    { id: "edit", label: "Modifier le mot", onSelect: onEdit },
    canRenameSpelling
      ? {
          id: "rename",
          label: "Modifier l'orthographe du mot",
          onSelect: onRenameSpelling,
        }
      : null,
    canDeleteWord
      ? {
          id: "delete",
          label: "Supprimer le mot",
          onSelect: onDelete,
          danger: true,
        }
      : null,
  ].filter(Boolean);

  const handleSelect = (option) => {
    setOpen(false);
    option.onSelect?.();
  };

  return (
    <div
      className={`word-edit-actions-menu${open ? " word-edit-actions-menu--open" : ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="word-edit-actions-menu__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="word-edit-actions-menu__label">Actions d'édition</span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className="word-edit-actions-menu__chevron"
          aria-hidden="true"
        />
      </button>

      {open && coords
        ? createPortal(
            <ul
              ref={listRef}
              className="word-edit-actions-menu__list"
              role="listbox"
              style={{
                position: "fixed",
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                width: `${coords.width}px`,
                transform: coords.transform,
                transformOrigin: "top left",
              }}
            >
              {options.map((option) => (
                <li key={option.id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className={`word-edit-actions-menu__option${
                      option.danger ? " word-edit-actions-menu__option--danger" : ""
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            getAppPortalRoot()
          )
        : null}
    </div>
  );
}

export default function WordEditPanel({
  mot,
  onWordDeleted,
  onWordRenamed,
  onListRefresh,
  forceSkeleton = false,
}) {
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [previewMasked, setPreviewMasked] = useState(false);
  const [illustrationDataUrl, setIllustrationDataUrl] = useState(null);
  const [illustrationBusy, setIllustrationBusy] = useState(false);
  const illustrationInputRef = useRef(null);
  const [formData, setFormData] = useState({
    definition: "",
    extrait: "",
    motsCaches: [],
    livre: "",
    auteur: "",
    groupementId: "",
  });
  const [groupements, setGroupements] = useState([]);
  const [groupementBusy, setGroupementBusy] = useState(false);
  const taylorGothicReady = useTaylorGothicReady();

  useEffect(() => {
    if (!mot) return;
    let cancelled = false;

    const fetchDetails = async () => {
      const url = `${API_URL}/mots/${encodeURIComponent(mot)}`;
      try {
        const [res, illustration] = await Promise.all([
          fetch(url),
          fetchWordIllustration(mot).catch(() => null),
        ]);
        const json = await res.json();
        if (cancelled) return;
        setData(json);
        setIllustrationDataUrl(illustration);
        setFormData(ficheFormFromWord(json));
        setIsEditing(false);
        setPreviewMasked(false);
      } catch (err) {
        if (!cancelled) console.error("Erreur WordEditPanel:", err);
      }
    };

    fetchDetails();
    fetchGroupements()
      .then((items) => {
        if (!cancelled) {
          setGroupements(
            [...items].sort((a, b) =>
              String(a.title || "").localeCompare(String(b.title || ""), "fr", {
                sensitivity: "base",
              })
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) setGroupements([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mot]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGroupementChange = useCallback(
    async (nextGroupementId) => {
      if (groupementBusy) return;
      const previous = formData.groupementId || "";
      const next = nextGroupementId || "";
      if (next === previous) return;

      setFormData((current) => ({ ...current, groupementId: next }));
      setGroupementBusy(true);
      try {
        const updated = await assignWordGroupement(mot, next || null);
        setData((current) => ({ ...(current || {}), ...updated }));
      } catch (error) {
        setFormData((current) => ({ ...current, groupementId: previous }));
        alert(error.message || "Impossible d'enregistrer le groupement thématique.");
      } finally {
        setGroupementBusy(false);
      }
    },
    [formData.groupementId, groupementBusy, mot]
  );

  const filledMotsCachesCount = formData.motsCaches.filter((entry) =>
    entry.trim()
  ).length;

  const updateMotCacheRow = (index, value) => {
    setFormData((prev) => {
      if (isBaseMotCache(prev.motsCaches[index], mot)) return prev;
      if (motCacheAlreadyUsed(prev.motsCaches, index, value)) return prev;
      return {
        ...prev,
        motsCaches: prev.motsCaches.map((entry, rowIndex) =>
          rowIndex === index ? value : entry
        ),
      };
    });
  };

  const addMotCacheRow = () => {
    if (formData.motsCaches.length >= WORD_CACHES_MAX) return;
    if (formData.motsCaches.some((entry) => !entry.trim())) return;

    setFormData((prev) => ({
      ...prev,
      motsCaches: [...prev.motsCaches, ""],
    }));
  };

  const removeMotCacheRow = (index) => {
    if (formData.motsCaches.length <= 1) return;
    if (isBaseMotCache(formData.motsCaches[index], mot)) return;

    setFormData((prev) => ({
      ...prev,
      motsCaches: prev.motsCaches.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const startEditing = () => {
    setPreviewMasked(false);
    setFormData((prev) => ({
      ...prev,
      motsCaches: syncBaseMotCache(
        prev.motsCaches.length ? [...prev.motsCaches] : [""],
        mot
      ),
    }));
    setIsEditing(true);
  };

  const showMaskedPreview = previewMasked && !isEditing;
  const definitionToShow = showMaskedPreview
    ? maskDefinition(formData.definition, formData.motsCaches)
    : formData.definition;
  const extraitToShow = showMaskedPreview
    ? maskDefinition(formData.extrait, formData.motsCaches)
    : formData.extrait;

  const canAddMotCacheRow =
    formData.motsCaches.length < WORD_CACHES_MAX &&
    formData.motsCaches.every((entry) => entry.trim().length > 0);

  const isFormValid = !validateWordFicheFields({
    mot,
    auteur: formData.auteur,
    livre: formData.livre,
    definition: formData.definition,
    extrait: formData.extrait,
    motsCaches: formData.motsCaches,
  });

  const handleSave = async () => {
    if (!isFormValid || saving) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          prepareWordFicheWrite({
            auteur: formData.auteur,
            livre: formData.livre,
            definition: formData.definition,
            extrait: formData.extrait,
            motsCaches: formData.motsCaches,
          })
        ),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(result.message || "Erreur lors de la modification du mot.");
        return;
      }

      const updated = result.mot || {
        ...data,
        auteur: formData.auteur.trim(),
        livre: formData.livre.trim(),
        definition: formData.definition.trim(),
        extrait: formData.extrait.trim(),
        motsCaches: formData.motsCaches.map((m) => m.toLowerCase()),
      };

      setData(updated);
      setFormData(ficheFormFromWord(updated, formData.groupementId));
      setIsEditing(false);
    } catch (err) {
      console.error("Erreur sauvegarde WordEditPanel:", err);
      alert("Erreur de connexion au serveur.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Réinitialiser avec les données originales
    if (data) {
      setFormData(ficheFormFromWord(data, formData.groupementId));
    }
    setIsEditing(false);
  };

  const handleAddIllustrationClick = () => {
    if (illustrationBusy || illustrationDataUrl) return;
    illustrationInputRef.current?.click();
  };

  const handleIllustrationSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !mot) return;

    setIllustrationBusy(true);
    try {
      const dataUrl = await prepareWordIllustration(file);
      const savedDataUrl = await uploadWordIllustration(mot, dataUrl);
      setIllustrationDataUrl(savedDataUrl);
    } catch (err) {
      console.error("Erreur ajout illustration :", err);
      alert(err.message || "Impossible d'ajouter l'illustration.");
    } finally {
      setIllustrationBusy(false);
    }
  };

  const handleDeleteIllustration = async () => {
    if (!mot || !illustrationDataUrl || illustrationBusy) return;

    setIllustrationBusy(true);
    try {
      await deleteWordIllustration(mot);
      setIllustrationDataUrl(null);
    } catch (err) {
      console.error("Erreur suppression illustration :", err);
      alert(err.message || "Impossible de supprimer l'illustration.");
    } finally {
      setIllustrationBusy(false);
    }
  };

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleWordDeleted = useCallback(() => {
    setShowDeleteModal(false);
    if (onWordDeleted) onWordDeleted(data?.mot || mot);
  }, [onWordDeleted, data?.mot, mot]);

  const deleteModal = (
    <DeleteWordModal
      word={data?.mot || mot}
      open={showDeleteModal}
      onClose={closeDeleteModal}
      onDeleted={handleWordDeleted}
    />
  );

  if (!mot) {
    return (
      <>
        <div className="word-edit-panel word-edit-panel--empty" />
        {deleteModal}
      </>
    );
  }

  if (forceSkeleton || !data || data.mot !== mot || !taylorGothicReady) {
    return (
      <>
        <WordEditSkeleton />
        {deleteModal}
      </>
    );
  }

  const motChars = data.mot ? Array.from(data.mot) : [];
  const motInitial = (motChars[0] ?? "").toUpperCase();
  const motRest = motChars.slice(1).join("");
  // Majuscule accentuée : TaylorGothic ne la gère pas → repli sur UnifrakturCook.
  const initialIsAccented = /[\u0300-\u036f]/.test(motInitial.normalize("NFD"));

  return (
    <div className="word-edit-panel">
      <h2 className="word-detail-title" style={{ 
        textAlign: "center", 
        margin: "0px 0 10px 0", 
        fontSize: "calc(26px * var(--text-scale, 1))", 
        color: "#39210d" 
      }}>
        <span
          className={`word-detail-title__initial${
            initialIsAccented ? " word-detail-title__initial--accented" : ""
          }`}
        >
          {motInitial}
        </span>
        {motRest}
      </h2>

      <GroupementAssignDropdown
        className="word-edit-groupement"
        groupements={groupements}
        value={formData.groupementId || ""}
        onChange={handleGroupementChange}
        disabled={!groupements.length || groupementBusy}
      />

      <div style={{ maxWidth: "700px", margin: "0 auto", paddingLeft: "15px", boxSizing: "border-box" }}>
        <div className="word-edit-source-row">
          <div className="word-edit-source-fields">
            <div style={{ marginBottom: "20px" }}>
              <label className="word-edit-field-title">
                Ouvrage
              </label>
              {isEditing ? (
                <input
                  type="text"
                  className="word-edit-field"
                  value={formData.livre}
                  maxLength={WORD_LIVRE_MAX_LENGTH}
                  onChange={(e) =>
                    handleChange("livre", e.target.value.slice(0, WORD_LIVRE_MAX_LENGTH))
                  }
                />
              ) : (
                <p className="word-edit-field-body" style={{ fontStyle: "italic" }}>"{formData.livre}"</p>
              )}
            </div>

            <div style={{ marginBottom: "0" }}>
              <label className="word-edit-field-title">
                Auteur
              </label>
              {isEditing ? (
                <input
                  type="text"
                  className="word-edit-field"
                  value={formData.auteur}
                  maxLength={WORD_AUTEUR_MAX_LENGTH}
                  onChange={(e) =>
                    handleChange("auteur", e.target.value.slice(0, WORD_AUTEUR_MAX_LENGTH))
                  }
                />
              ) : (
                <p className="word-edit-field-body">{formData.auteur}</p>
              )}
            </div>
          </div>

          <div className="word-edit-illustration">
            <div
              className={`word-edit-illustration__frame${
                illustrationDataUrl ? " word-edit-illustration__frame--filled" : ""
              }${!illustrationDataUrl && !illustrationBusy ? " word-edit-illustration__frame--clickable" : ""}`}
              role={!illustrationDataUrl ? "button" : undefined}
              tabIndex={!illustrationDataUrl && !illustrationBusy ? 0 : undefined}
              aria-label={
                illustrationDataUrl
                  ? "Illustration du mot"
                  : "Ajouter une illustration"
              }
              onClick={!illustrationDataUrl ? handleAddIllustrationClick : undefined}
              onKeyDown={
                !illustrationDataUrl
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleAddIllustrationClick();
                      }
                    }
                  : undefined
              }
            >
              {illustrationDataUrl ? (
                <img
                  src={illustrationDataUrl}
                  alt={`Illustration de ${data.mot}`}
                  className="word-edit-illustration__image"
                />
              ) : (
                <span className="word-edit-illustration__placeholder">
                  {illustrationBusy
                    ? "Traitement…"
                    : "Cliquez ici pour ajouter une illustration"}
                </span>
              )}
            </div>

            {illustrationDataUrl && (
              <div className="word-edit-illustration__remove-anchor">
                <MenuIconTooltip
                  tooltip="Supprimer l'illustration"
                  placement="above"
                  portal
                >
                  <button
                    type="button"
                    className="word-edit-illustration__remove"
                    onClick={handleDeleteIllustration}
                    disabled={illustrationBusy}
                    aria-label="Supprimer l'illustration"
                  >
                    <X size={16} strokeWidth={2.5} />
                  </button>
                </MenuIconTooltip>
              </div>
            )}

            <input
              ref={illustrationInputRef}
              type="file"
              accept={ILLUSTRATION_ACCEPT}
              className="word-edit-illustration__file-input"
              onChange={handleIllustrationSelected}
            />
          </div>
        </div>

        {/* Définition */}
        <div data-scroll-anchor="definition" style={{ marginBottom: "20px" }}>
          <label className="word-edit-field-title-row" htmlFor="word-edit-definition">
            <span className="word-edit-field-title">
              Définition
            </span>
            {isEditing && (
              <WordFieldCharCount
                value={formData.definition}
                maxLength={WORD_RICH_TEXT_MAX_LENGTH}
              />
            )}
          </label>
          {isEditing ? (
            <textarea
              id="word-edit-definition"
              className="word-edit-field"
              value={formData.definition}
              onChange={(e) => handleChange('definition', e.target.value)}
              rows={8}
            />
          ) : (
            <div
              className="word-edit-field-body"
              style={{
                whiteSpace: "pre-line",
                background: "#faf0e6",
                borderRadius: "4px",
              }}
            >
              {definitionToShow}
            </div>
          )}
        </div>

        {/* Extrait */}
        <div data-scroll-anchor="extrait" style={{ marginBottom: "20px" }}>
          <label className="word-edit-field-title-row" htmlFor="word-edit-extrait">
            <span className="word-edit-field-title">
              Extrait
            </span>
            {isEditing && (
              <WordFieldCharCount
                value={formData.extrait}
                maxLength={WORD_RICH_TEXT_MAX_LENGTH}
              />
            )}
          </label>
          {isEditing ? (
            <textarea
              id="word-edit-extrait"
              className="word-edit-field"
              value={formData.extrait}
              onChange={(e) => handleChange('extrait', e.target.value)}
              rows={10}
            />
          ) : (
            <div
              className="word-edit-field-body"
              style={{
                whiteSpace: "pre-line",
                background: "#faf0e6",
                borderRadius: "4px",
                fontStyle: "italic",
              }}
            >
              <LiteraryExcerpt text={extraitToShow} />
            </div>
          )}
        </div>

        {/* Mots cachés */}
        <div style={{ marginBottom: "20px" }}>
          <div className="word-edit-mots-caches-header">
            <span className="word-edit-field-title word-edit-mots-caches-header__title">
              Mots cachés
            </span>
            {!isEditing && (
              <div
                className="word-edit-mots-caches-preview"
                aria-label="Aperçu du masquage des mots cachés"
              >
                <Eye
                  size={18}
                  strokeWidth={2}
                  aria-hidden="true"
                  className={
                    previewMasked
                      ? "word-edit-mots-caches-preview__eye"
                      : "word-edit-mots-caches-preview__eye word-edit-mots-caches-preview__eye--active"
                  }
                />
                <ToggleSwitch
                  checked={previewMasked}
                  onChange={setPreviewMasked}
                  compact
                  alwaysUncheckedLook
                />
                <EyeOff
                  size={18}
                  strokeWidth={2}
                  aria-hidden="true"
                  className={
                    previewMasked
                      ? "word-edit-mots-caches-preview__eye word-edit-mots-caches-preview__eye--active"
                      : "word-edit-mots-caches-preview__eye"
                  }
                />
              </div>
            )}
          </div>
          {isEditing ? (
            <div className="word-edit-mots-caches">
              {formData.motsCaches.map((entry, index) => (
                <div key={`mot-cache-${index}`} className="word-edit-mots-caches__row">
                  <input
                    type="text"
                    value={entry}
                    onChange={(e) => updateMotCacheRow(index, e.target.value)}
                    className="word-edit-mots-caches__input"
                    placeholder={`Mot caché ${index + 1}`}
                    maxLength={WORD_CACHE_MAX_LENGTH}
                    readOnly={isBaseMotCache(entry, mot)}
                  />
                  <button
                    type="button"
                    className="word-edit-mots-caches__remove"
                    onClick={() => removeMotCacheRow(index)}
                    disabled={
                      formData.motsCaches.length <= 1 ||
                      isBaseMotCache(entry, mot)
                    }
                    aria-label={`Supprimer le mot caché ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              ))}

              <div className="word-edit-mots-caches__footer">
                <button
                  type="button"
                  className="word-edit-mots-caches__add"
                  onClick={addMotCacheRow}
                  disabled={!canAddMotCacheRow}
                  aria-label="Ajouter un mot caché"
                >
                  +
                </button>
                <span className="word-edit-mots-caches__hint">
                  {filledMotsCachesCount}/{WORD_CACHES_MAX} mots cachés
                </span>
              </div>
            </div>
          ) : (
            <p className="word-edit-field-body">
              {formData.motsCaches.length > 0
                ? formData.motsCaches.join(" · ")
                : "—"}
            </p>
          )}
        </div>

        {/* Actions */}
        {!isEditing ? (
          <EditActionsMenu
            onEdit={startEditing}
            onRenameSpelling={() => setShowRenameModal(true)}
            onDelete={() => setShowDeleteModal(true)}
            canRenameSpelling
            canDeleteWord={data.deletable !== false && !data.renamedFrom}
          />
        ) : (
          <div className="word-edit-actions">
            <button
              type="button"
              className="word-edit-btn word-edit-btn--save"
              onClick={handleSave}
              disabled={!isFormValid || saving}
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button
              type="button"
              className="word-edit-btn word-edit-btn--cancel"
              onClick={handleCancel}
            >
              Annuler
            </button>
          </div>
        )}
      </div>

      {data.deletable !== false && !data.renamedFrom ? deleteModal : null}

      <RenameWordSpellingModal
        word={data.mot}
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        onRenamed={(oldMot, newMot) => {
          setShowRenameModal(false);
          if (onWordRenamed) {
            onWordRenamed(oldMot, newMot);
            return;
          }
          if (onListRefresh) onListRefresh();
        }}
      />
    </div>
  );
}